"use client";

import { useTranslations } from "next-intl";
import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { useSignTypedData, useWriteContract } from "wagmi";
import { useAuth } from "@/context/auth";

// Wagmi v2 UseWriteContractReturnType does not expose mutate/mutateAsync at the type level
// (they exist at runtime as TanStack aliases). We extend the return type explicitly so callers
// that use the TanStack mutation shape are still type-safe. (BUG-11-CHG-01 Fix #4)
type GuardedWriteContractReturn = ReturnType<typeof useWriteContract> & {
  mutate: ReturnType<typeof useWriteContract>["writeContract"];
  mutateAsync: ReturnType<typeof useWriteContract>["writeContractAsync"];
};

type GuardedSignTypedDataReturn = ReturnType<typeof useSignTypedData> & {
  mutate: ReturnType<typeof useSignTypedData>["signTypedData"];
  mutateAsync: ReturnType<typeof useSignTypedData>["signTypedDataAsync"];
};

/**
 * Drop-in replacement for wagmi useWriteContract.
 * During impersonation: all 4 callable fields (writeContract, writeContractAsync,
 * mutate, mutateAsync) are intercepted — toast fires + call is no-op / rejected.
 *
 * Sonner id-dedup (Fix #6): rapid repeated calls each carry id "impersonation-write-blocked"
 * so only 1 toast shows at a time on screen.
 *
 * TanStack aliases (Fix #4): mutate === writeContract, mutateAsync === writeContractAsync.
 * All 4 must be overridden so any caller path triggers the guard.
 */
export function useGuardedWriteContract(): GuardedWriteContractReturn {
  const base = useWriteContract();
  const isImpersonating = useAuth((s) => s.isImpersonating);
  const t = useTranslations("errors");
  const blockedToast = t("impersonationWriteBlocked");

  const guardedSync = useCallback(
    (...args: Parameters<typeof base.writeContract>) => {
      if (isImpersonating) {
        toast.error(blockedToast, { id: "impersonation-write-blocked" });
        return undefined;
      }
      return base.writeContract(...args);
    },
    [isImpersonating, base.writeContract, blockedToast],
  ) as typeof base.writeContract;

  const guardedAsync = useCallback(
    async (...args: Parameters<typeof base.writeContractAsync>) => {
      if (isImpersonating) {
        toast.error(blockedToast, { id: "impersonation-write-blocked" });
        throw new Error("Impersonation: write blocked");
      }
      return base.writeContractAsync(...args);
    },
    [isImpersonating, base.writeContractAsync, blockedToast],
  ) as typeof base.writeContractAsync;

  return useMemo(
    () => ({
      ...base,
      writeContract: guardedSync,
      writeContractAsync: guardedAsync,
      // TanStack mutation aliases — must point at guarded functions (Fix #4)
      mutate: guardedSync,
      mutateAsync: guardedAsync,
    }),
    [base, guardedSync, guardedAsync],
  );
}

/**
 * Drop-in replacement for wagmi useSignTypedData.
 * During impersonation: all 4 callable fields (signTypedData, signTypedDataAsync,
 * mutate, mutateAsync) are intercepted.
 *
 * Sonner id-dedup (Fix #6): toast id "impersonation-sign-blocked".
 */
export function useGuardedSignTypedData(): GuardedSignTypedDataReturn {
  const base = useSignTypedData();
  const isImpersonating = useAuth((s) => s.isImpersonating);
  const t = useTranslations("errors");
  const blockedToast = t("impersonationWriteBlocked");

  const guardedSyncSign = useCallback(
    (...args: Parameters<typeof base.signTypedData>) => {
      if (isImpersonating) {
        toast.error(blockedToast, { id: "impersonation-sign-blocked" });
        return undefined;
      }
      return base.signTypedData(...args);
    },
    [isImpersonating, base.signTypedData, blockedToast],
  ) as typeof base.signTypedData;

  const guardedAsyncSign = useCallback(
    async (...args: Parameters<typeof base.signTypedDataAsync>) => {
      if (isImpersonating) {
        toast.error(blockedToast, { id: "impersonation-sign-blocked" });
        throw new Error("Impersonation: sign blocked");
      }
      return base.signTypedDataAsync(...args);
    },
    [isImpersonating, base.signTypedDataAsync, blockedToast],
  ) as typeof base.signTypedDataAsync;

  return useMemo(
    () => ({
      ...base,
      signTypedData: guardedSyncSign,
      signTypedDataAsync: guardedAsyncSign,
      // TanStack mutation aliases — must point at guarded functions (Fix #4)
      mutate: guardedSyncSign,
      mutateAsync: guardedAsyncSign,
    }),
    [base, guardedSyncSign, guardedAsyncSign],
  );
}
